import { Stack } from "@certn/cascadia/foundations/grid-layout/Stack";
import { Text } from "@certn/cascadia/foundations/typography/Text";
import { lightColors } from "@certn/cascadia-tokens";
import { css, cx } from "@emotion/css";
import debounce from "lodash/debounce";
import { useRef, useState, useEffect, type RefObject } from "react";
import { Trans, useTranslation } from "react-i18next";
import SignatureCanvas from "react-signature-canvas";

import { useSignature } from "./useSignature";
import { Link } from "../link/Link";

/** Always-white export canvas; overlay colors use light tokens so dark-mode UI stays AA-safe. */
export const SIGNATURE_WHITE_CAPTURE_SURFACE = "#ffffff" as const;
export const SIGNATURE_CLEAR_LABEL_ON_WHITE_CAPTURE_SURFACE =
  lightColors["text-info"];
export const SIGNATURE_GUIDE_LINE_ON_WHITE_CAPTURE_SURFACE =
  lightColors["border-strong"];
export interface SignatureProps {
  /**
   * The width of the signature pad in pixels.
   * If not provided, the signature pad will be responsive and fill its container width.
   */
  width?: number;
  /**
   * The height of the signature pad
   */
  height?: number;
  /**
   * The minimum width of the signature pad when in responsive mode (width not provided).
   * Defaults to 200px.
   */
  minWidth?: number;
  /**
   * The color of the signature
   */
  penColor?: string;
  /**
   * The thickness of the signature line
   */
  penWidth?: number;
  /**
   * Callback function that is called when the signature changes
   */
  onChange?: (signature: string) => void;
  /**
   * Callback function that is called when the signature is cleared
   */
  onClear?: () => void;
  /**
   * Whether the signature pad is disabled
   */
  disabled?: boolean;
  /**
   * Additional class name for the component
   */
  className?: string;
  /**
   * A data URL of a previously drawn signature to restore on mount.
   * Useful for preserving the signature across component remounts
   * (e.g. when toggling between expanded and collapsed views).
   */
  value?: string | null;
  /**
   * Optional description text shown below the "Signature required" heading.
   * Defaults to instructions about drawing with mouse/touchpad.
   */
  description?: string;
  /**
   * When true, the signature canvas uses an opaque white background so dark ink
   * exports correctly (e.g. SuperDoc places signatures on a white card).
   * Overlay controls (Clear, guide line) use contrast-safe colors for that surface.
   */
  opaqueWhiteCaptureSurface?: boolean;
}

interface SignatureContainerProps {
  width?: number;
  height: number;
  minWidth: number;
  className?: string;
  opaqueWhiteCaptureSurface?: boolean;
  children: (measuredWidth: number) => React.ReactNode;
}

function SignatureContainer({
  width,
  height,
  minWidth,
  className,
  opaqueWhiteCaptureSurface = false,
  children,
}: SignatureContainerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number>(width ?? minWidth);
  const isResponsive = width === undefined;

  useEffect(() => {
    if (!isResponsive || !containerRef.current) return;

    // Initial measurement
    const measureWidth = () => {
      if (containerRef.current) {
        const newWidth = Math.max(containerRef.current.offsetWidth, minWidth);
        setMeasuredWidth(newWidth);
      }
    };

    measureWidth();

    const debouncedMeasure = debounce(measureWidth, 100);

    const observer = new ResizeObserver(() => {
      debouncedMeasure();
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      debouncedMeasure.cancel();
    };
  }, [isResponsive, minWidth]);

  // For fixed width, use the provided width
  const effectiveWidth = isResponsive ? measuredWidth : width;

  return (
    <div
      ref={containerRef}
      style={{
        width: isResponsive ? "100%" : `${width}px`,
        minWidth: `${minWidth}px`,
        height: `${height}px`,
      }}
      className={css(
        className,
        styles.container,
        opaqueWhiteCaptureSurface && styles.containerOnWhiteSurface
      )}
      data-testid="signature-pad-container"
    >
      {children(effectiveWidth)}
    </div>
  );
}

interface SignaturePadProps {
  measuredWidth: number;
  height: number;
  penColor: string;
  penWidth: number;
  disabled: boolean;
  opaqueWhiteCaptureSurface: boolean;
  signatureRef: RefObject<SignatureCanvas | null>;
  lastCapturedDataUrlRef: RefObject<string | null>;
  value?: string | null;
  hasSignature: boolean;
  onEnd: () => void;
  onClear: () => void;
}

/**
 * Responsive pads remeasure after mount; changing canvas width clears the bitmap.
 * When exporting on a white SuperDoc card, refill the opaque white surface after
 * each width change so dark ink stays visible in dark mode and exports correctly.
 */
function SignaturePad({
  measuredWidth,
  height,
  penColor,
  penWidth,
  disabled,
  opaqueWhiteCaptureSurface,
  signatureRef,
  lastCapturedDataUrlRef,
  value,
  hasSignature,
  onEnd,
  onClear,
}: SignaturePadProps) {
  useEffect(() => {
    if (!opaqueWhiteCaptureSurface) return;

    const pad = signatureRef.current;
    if (!pad) return;

    const canvas = pad.getCanvas();
    const restoreUrl = value ?? lastCapturedDataUrlRef.current;

    if (restoreUrl) {
      pad.fromDataURL(restoreUrl, {
        width: canvas.width,
        height: canvas.height,
      });
      return;
    }

    pad.clear();
  }, [
    measuredWidth,
    opaqueWhiteCaptureSurface,
    value,
    signatureRef,
    lastCapturedDataUrlRef,
  ]);

  return (
    <>
      <SignatureCanvas
        ref={signatureRef}
        penColor={penColor}
        minWidth={penWidth}
        maxWidth={penWidth}
        clearOnResize={false}
        backgroundColor={
          opaqueWhiteCaptureSurface
            ? SIGNATURE_WHITE_CAPTURE_SURFACE
            : "transparent"
        }
        canvasProps={{
          width: measuredWidth,
          height,
          className: cx("signature-canvas", styles.canvas),
          style: {
            pointerEvents: disabled ? "none" : "auto",
            opacity: disabled ? 0.5 : 1,
          },
        }}
        onEnd={onEnd}
      />
      <SignatureLine onOpaqueWhiteCaptureSurface={opaqueWhiteCaptureSurface} />
      <ClearLink
        onPress={onClear}
        isDisabled={disabled || !hasSignature}
        onOpaqueWhiteCaptureSurface={opaqueWhiteCaptureSurface}
      />
    </>
  );
}

interface OverlayControlProps {
  onOpaqueWhiteCaptureSurface: boolean;
}

function SignatureLine({ onOpaqueWhiteCaptureSurface }: OverlayControlProps) {
  return (
    <div
      data-testid="signature-guide-line"
      className={css(
        styles.signatureLine,
        onOpaqueWhiteCaptureSurface && styles.signatureLineOnWhiteSurface
      )}
    />
  );
}
interface ClearButtonProps {
  onPress: () => void;
  isDisabled: boolean;
}

function ClearLink({
  onPress,
  isDisabled,
  onOpaqueWhiteCaptureSurface,
}: ClearButtonProps & OverlayControlProps) {
  return (
    <Link
      onPress={onPress}
      isDisabled={isDisabled}
      className={css(
        styles.clearLink,
        styles.clearLinkState,
        onOpaqueWhiteCaptureSurface && styles.clearLinkOnWhiteSurface,
        {
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled ? 0.5 : 1,
        }
      )}
      testID="clear-button"
    >
      Clear
    </Link>
  );
}

// TODO: update this component to use the designs from this issues https://certn.atlassian.net/browse/FP-277
export function Signature({
  width,
  height = 150,
  minWidth = 200,
  penColor = "#000",
  penWidth = 1,
  onChange,
  onClear,
  disabled = false,
  className,
  value,
  description,
  opaqueWhiteCaptureSurface = false,
}: SignatureProps) {
  const { t } = useTranslation();
  const {
    signatureRef,
    lastCapturedDataUrlRef,
    hasSignature,
    handleSignatureChange,
    handleClear,
  } = useSignature({
    onChange,
    onClear,
    value,
  });

  return (
    <Stack gap="space-4">
      <Text style="form/section-heading">
        <Trans>Signature required</Trans>
      </Text>
      <Text style="form/body-primary">
        {description ??
          t(
            "Draw your signature using your mouse or touchpad in the box below."
          )}
      </Text>

      <SignatureContainer
        width={width}
        height={height}
        minWidth={minWidth}
        className={className}
        opaqueWhiteCaptureSurface={opaqueWhiteCaptureSurface}
      >
        {(measuredWidth) => (
          <SignaturePad
            measuredWidth={measuredWidth}
            height={height}
            penColor={penColor}
            penWidth={penWidth}
            disabled={disabled}
            opaqueWhiteCaptureSurface={opaqueWhiteCaptureSurface}
            signatureRef={signatureRef}
            lastCapturedDataUrlRef={lastCapturedDataUrlRef}
            value={value}
            hasSignature={hasSignature}
            onEnd={handleSignatureChange}
            onClear={handleClear}
          />
        )}
      </SignatureContainer>
    </Stack>
  );
}

const styles = {
  container: css({
    position: "relative",
    border: "1px solid #ccc",
    borderRadius: "4px",
    backgroundColor: "var(--background-input)",
  }),
  containerOnWhiteSurface: css({
    backgroundColor: SIGNATURE_WHITE_CAPTURE_SURFACE,
  }),
  signatureLine: css({
    position: "absolute",
    bottom: "20px",
    left: "20px",
    right: "20px",
    height: "1px",
    zIndex: 1,
    pointerEvents: "none",
    backgroundColor: "var(--border-base)",
  }),
  signatureLineOnWhiteSurface: css({
    backgroundColor: SIGNATURE_GUIDE_LINE_ON_WHITE_CAPTURE_SURFACE,
  }),
  clearLink: css({
    position: "absolute",
    top: "8px",
    right: "8px",
    fontSize: "12px",
    zIndex: 2,
  }),
  clearLinkState: css({
    "&[disabled]": {
      cursor: "not-allowed",
      opacity: 0.5,
    },
    "&:not([disabled])": {
      cursor: "pointer",
      opacity: 1,
    },
  }),
  clearLinkOnWhiteSurface: css({
    color: `${SIGNATURE_CLEAR_LABEL_ON_WHITE_CAPTURE_SURFACE} !important`,
  }),
  canvas: css({
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  }),
};
